import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, MailOpen, Search, Trash2, Loader2, AlertCircle,
  CheckCheck, X, Eye, EyeOff, MailX,
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationsContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import type { NotificationsResp } from '../../hooks/useMyNotifications';

/* ── helpers ── */
function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} - ${hh}:${min}h.`;
}

/* ── component ── */
export default function NotificationsPage() {
  const { user } = useAuth();
  const { loading, error, markRead, markUnread, markAllRead, bulkAction, remove, reload } =
    useNotifications();

  /* local search + data (fetch diretto per includere il parametro q) */
  const [search, setSearch] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [localData, setLocalData] = useState<NotificationsResp | null>(null);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLocalLoading(true);
    const params: Record<string, string> = { limit: '200' };
    if (debouncedQ) params.q = debouncedQ;
    api
      .get<NotificationsResp>('/api/clienti/me/notifications', { params })
      .then((r) => { if (!cancelled) setLocalData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLocalLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQ, loading]); // ricarica anche dopo azioni

  const items = localData?.items ?? [];
  const unreadCount = localData?.unreadCount ?? 0;
  const total = localData?.total ?? 0;

  /* ── checkbox state ── */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const allSelected = items.length > 0 && items.every((n) => selected.has(n.id));
  const someSelected = selected.size > 0;

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(items.map((n) => n.id)));

  /* ── bulk actions ── */
  const handleBulk = async (action: 'read' | 'unread' | 'delete') => {
    if (!someSelected) return;
    await bulkAction(Array.from(selected), action);
    setSelected(new Set());
  };

  const handleRemove = async (id: string) => {
    await remove(id);
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };

  const nome = user?.nome ?? user?.email ?? 'Cliente';

  return (
    <div data-testid="notifications-page" className="space-y-4">
      {/* ── Intestazione ── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Mail className="w-5 h-5 text-[hsl(210,64%,29%)]" />
            Messaggi
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} messaggi salvati · {unreadCount > 0 ? `${unreadCount} non letti` : 'tutti letti'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            disabled={loading}
            data-testid="mark-all-read"
            className="h-9 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(0,0%,97%)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCheck className="w-4 h-4" /> Segna tutto come letto
          </button>
        )}
      </div>

      {/* ── Banner non letti ── */}
      {unreadCount > 0 && (
        <div
          data-testid="unread-banner"
          className="rounded-xl border border-[hsl(210,64%,29%)] bg-[hsl(210,64%,97%)] px-4 py-3 flex items-center gap-3 text-sm"
        >
          <Mail className="w-5 h-5 text-[hsl(210,64%,29%)] flex-shrink-0" />
          <span>
            <strong>{nome}</strong>, hai{' '}
            <strong>{unreadCount} {unreadCount === 1 ? 'messaggio non letto' : 'messaggi non letti'}</strong>.
          </span>
          <Mail className="w-5 h-5 text-[hsl(210,64%,29%)] flex-shrink-0 ml-auto" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" data-testid="notif-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* ── Barra ricerca ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} messaggi salvati
        </span>
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ricerca per oggetto…"
            data-testid="notif-search"
            className="w-full h-9 pl-9 pr-8 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="notif-clear-search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ── Tabella ── */}
      <div className="rounded-xl border bg-white overflow-hidden">
        {(loading || localLoading) && (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="notif-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento messaggi…
          </div>
        )}

        {!loading && !localLoading && (
          <>
            <table className="w-full text-sm" data-testid="notif-table">
              <thead>
                <tr className="border-b bg-[hsl(var(--dtk-canvas,0,0%,98%))]">
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      data-testid="notif-select-all"
                      className="w-4 h-4 rounded accent-[hsl(210,64%,29%)]"
                    />
                  </th>
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase w-40">Data</th>
                  <th className="text-left px-3 py-3 text-[10px] font-semibold text-muted-foreground uppercase">Oggetto</th>
                  <th className="w-10 px-3 py-3 text-center text-[10px] font-semibold text-muted-foreground uppercase">Letto</th>
                  <th className="w-10 px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center" data-testid="notif-empty">
                      <MailX className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-semibold">
                        {search ? 'Nessun messaggio trovato per la ricerca.' : 'Nessun messaggio da mostrare.'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  items.map((n) => (
                    <tr
                      key={n.id}
                      data-testid={`notif-${n.id}`}
                      className={`border-b transition-colors hover:bg-[hsl(210,40%,98%)] ${
                        !n.read ? 'bg-[hsl(210,64%,99%)]' : ''
                      } ${selected.has(n.id) ? 'bg-[hsl(210,40%,96%)]' : ''}`}
                    >
                      {/* Checkbox */}
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(n.id)}
                          onChange={() => toggleOne(n.id)}
                          data-testid={`notif-check-${n.id}`}
                          className="w-4 h-4 rounded accent-[hsl(210,64%,29%)]"
                        />
                      </td>

                      {/* Data */}
                      <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(n.date)}
                      </td>

                      {/* Oggetto + messaggio */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {!n.read && (
                            <span className="w-2 h-2 rounded-full bg-[hsl(210,64%,29%)] flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className={`truncate ${!n.read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>
                              {n.link ? (
                                <Link
                                  to={n.link}
                                  onClick={() => !n.read && markRead(n.id)}
                                  className="hover:underline hover:text-[hsl(210,64%,29%)]"
                                  data-testid={`notif-link-${n.id}`}
                                >
                                  {n.title}
                                </Link>
                              ) : (
                                n.title
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">{n.message}</p>
                          </div>
                        </div>
                      </td>

                      {/* Stato letto */}
                      <td className="px-3 py-3 text-center">
                        <button
                          onClick={() => n.read ? markUnread(n.id) : markRead(n.id)}
                          data-testid={`notif-toggle-read-${n.id}`}
                          title={n.read ? 'Segna come non letto' : 'Segna come letto'}
                          className="p-1 rounded hover:bg-secondary/60 transition-colors"
                        >
                          {n.read
                            ? <MailOpen className="w-4 h-4 text-muted-foreground" />
                            : <Mail className="w-4 h-4 text-[hsl(210,64%,29%)]" />}
                        </button>
                      </td>

                      {/* Elimina */}
                      <td className="px-3 py-3">
                        <button
                          onClick={() => handleRemove(n.id)}
                          data-testid={`notif-delete-${n.id}`}
                          title="Elimina messaggio"
                          className="p-1 rounded text-muted-foreground hover:text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* ── Barra azioni massive ── */}
            {someSelected && (
              <div
                data-testid="notif-bulk-bar"
                className="px-4 py-3 border-t bg-[hsl(210,64%,29%)] text-white flex items-center gap-3 flex-wrap"
              >
                <span className="text-sm font-medium">
                  {selected.size} {selected.size === 1 ? 'selezionato' : 'selezionati'}
                </span>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => handleBulk('read')}
                    data-testid="bulk-mark-read"
                    className="h-8 px-3 rounded-lg text-xs font-medium bg-white/20 hover:bg-white/30 inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Segna come letto
                  </button>
                  <button
                    onClick={() => handleBulk('unread')}
                    data-testid="bulk-mark-unread"
                    className="h-8 px-3 rounded-lg text-xs font-medium bg-white/20 hover:bg-white/30 inline-flex items-center gap-1.5 transition-colors"
                  >
                    <EyeOff className="w-3.5 h-3.5" /> Segna come non letto
                  </button>
                  <button
                    onClick={() => handleBulk('delete')}
                    data-testid="bulk-delete"
                    className="h-8 px-3 rounded-lg text-xs font-medium bg-rose-500 hover:bg-rose-600 inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Elimina
                  </button>
                  <button
                    onClick={() => setSelected(new Set())}
                    data-testid="bulk-deselect"
                    className="h-8 w-8 rounded-lg text-xs font-medium bg-white/20 hover:bg-white/30 inline-flex items-center justify-center transition-colors"
                    title="Deseleziona tutto"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
